import React, {useEffect, useMemo, useRef, useState} from 'react';

const WINDOW = 3;

const STEPS = [
  {
    key: 'learning',
    label: 'Learn',
    icon: '📖',
    number: 1,
    borderColor: 'var(--lcv-blue)',
    background: 'rgba(66, 165, 245, 0.12)',
  },
  {
    key: 'workbook',
    label: 'Work',
    icon: '✏️',
    number: 2,
    borderColor: 'var(--lcv-orange)',
    background: 'rgba(255, 167, 38, 0.12)',
  },
  {
    key: 'knowledge_check',
    label: 'Check',
    icon: '🎯',
    number: 3,
    borderColor: 'var(--lcv-green)',
    background: 'rgba(102, 187, 106, 0.12)',
  },
];

function getEncouragement(stats, termsDone, totalTerms) {
  if (!stats || stats.answersTotal === 0) {
    return termsDone === 0
      ? "Let's get started!"
      : `${termsDone} of ${totalTerms} terms done`;
  }

  const accuracy = Math.round((stats.answersCorrect / stats.answersTotal) * 100);
  const lastFive = stats.answerTimestamps.slice(-5);
  const isRushing =
    lastFive.length >= 2 &&
    (lastFive[lastFive.length - 1] - lastFive[0]) / (lastFive.length - 1) < 3000 &&
    accuracy < 50;

  if (isRushing) return 'Slow down and read carefully.';
  if (termsDone >= totalTerms && accuracy >= 80) {
    return `All ${totalTerms} terms done at ${accuracy}%`;
  }
  if (accuracy >= 80) return `${accuracy}% accuracy - you're crushing it`;
  if (accuracy >= 60) return `${accuracy}% accuracy - solid work`;
  return `${accuracy}% accuracy - keep trying`;
}

const LCVProgressTracker = ({data, phase, mode = 'full'}) => {
  if (!data) return null;

  const {
    terms,
    activeTermIndex,
    activeSubPhase,
    introComplete,
    masteryReached,
    termsDone,
    stats,
  } = data;

  const totalSlots = terms.length;
  const [windowStart, setWindowStart] = useState(
    Math.max(0, activeTermIndex - (WINDOW - 1)),
  );
  const [hovered, setHovered] = useState(false);
  const [celebratingIds, setCelebratingIds] = useState([]);

  const getWindowStartForActiveState = () => {
    if (masteryReached) {
      return Math.max(0, totalSlots - WINDOW);
    }

    if (activeTermIndex < 0) return 0;
    return Math.max(0, Math.min(activeTermIndex - (WINDOW - 1), totalSlots - WINDOW));
  };

  const currentStepIndex = STEPS.findIndex(step => step.key === activeSubPhase);
  const isIntroActive = phase === 'frontloading';
  const isMasteryActive = phase === 'mastery' || phase === 'results';
  const isMasteryDone = phase === 'results';
  const isKnowledgeCheckSummary = phase === 'knowledge_check_summary';
  const isExpanded = mode === 'full' || hovered;

  useEffect(() => {
    if (masteryReached) {
      setWindowStart(Math.max(0, totalSlots - WINDOW));
      return;
    }

    setWindowStart(prev => {
      if (activeTermIndex < prev) return activeTermIndex;
      if (activeTermIndex >= prev + WINDOW) return activeTermIndex - (WINDOW - 1);
      return prev;
    });
  }, [activeTermIndex, masteryReached, totalSlots]);

  useEffect(() => {
    if (!isExpanded) {
      setWindowStart(getWindowStartForActiveState());
    }
  }, [isExpanded, activeTermIndex, masteryReached, totalSlots]);

  const canScrollPrev = windowStart > 0;
  const canScrollNext = windowStart + WINDOW < totalSlots;
  const visibleTermIndices = Array.from({length: WINDOW}, (_, i) => windowStart + i)
    .filter(i => i < totalSlots);
  const encouragement = getEncouragement(stats, termsDone, totalSlots);
  const getTermDone = termIndex =>
    masteryReached ||
    termIndex < activeTermIndex ||
    (isKnowledgeCheckSummary && termIndex === activeTermIndex);

  const doneStates = useMemo(
    () => ({
      intro: introComplete && !isIntroActive,
      mastery: isMasteryDone,
      ...terms.reduce((acc, _, termIndex) => {
        acc[`term-${termIndex}`] = getTermDone(termIndex);
        return acc;
      }, {}),
    }),
    [
      terms,
      introComplete,
      isIntroActive,
      isMasteryDone,
      masteryReached,
      activeTermIndex,
      isKnowledgeCheckSummary,
    ],
  );
  const previousDoneStatesRef = useRef(doneStates);

  useEffect(() => {
    const previousDoneStates = previousDoneStatesRef.current;
    const newlyCompleted = Object.keys(doneStates).filter(
      key => doneStates[key] && !previousDoneStates[key],
    );

    previousDoneStatesRef.current = doneStates;

    if (!newlyCompleted.length) return undefined;

    setCelebratingIds(current => Array.from(new Set([...current, ...newlyCompleted])));
    const clearTimer = window.setTimeout(() => {
      setCelebratingIds(current =>
        current.filter(id => !newlyCompleted.includes(id)),
      );
    }, 2000);

    return () => window.clearTimeout(clearTimer);
  }, [doneStates]);

  return (
    <div
      className={[
        'lcv-progress-bar',
        isExpanded ? 'lcv-progress-bar-expanded' : 'lcv-progress-bar-mini',
      ].join(' ')}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="lcv-pt-header">
        <span className="lcv-pt-title">Progress Tracker</span>
        <span className="lcv-pt-encourage">{encouragement}</span>
      </div>

      <div className="lcv-pt-body">
        <button
          type="button"
          className={[
            'lcv-pt-node',
            'lcv-pt-node-intro',
            isIntroActive ? 'lcv-pt-node-active' : '',
            introComplete && !isIntroActive ? 'lcv-pt-node-done' : '',
            celebratingIds.includes('intro') ? 'lcv-pt-node-complete-pop' : '',
          ].filter(Boolean).join(' ')}
          disabled
        >
          <div className="lcv-pt-node-header">
            <span className="lcv-pt-num">{introComplete && !isIntroActive ? '✓' : '📋'}</span>
            <span className="lcv-pt-name">Intro</span>
          </div>
        </button>

        <div className="lcv-pt-term-rail">
          <button
            type="button"
            className={[
              'lcv-pt-cycle-btn',
              !isExpanded ? 'lcv-pt-cycle-spacer' : '',
              canScrollPrev ? '' : 'lcv-pt-cycle-hidden',
            ].filter(Boolean).join(' ')}
            onClick={() => setWindowStart(start => Math.max(0, start - 1))}
            disabled={!canScrollPrev || !isExpanded}
            aria-label="Show previous terms"
          >
            ‹
          </button>

          <div className="lcv-pt-terms">
            {visibleTermIndices.map(termIndex => {
              const term = terms[termIndex];
              const termDone = getTermDone(termIndex);
              const termActive =
                !isIntroActive &&
                !isMasteryActive &&
                !isKnowledgeCheckSummary &&
                termIndex === activeTermIndex;
              const termLocked = !masteryReached && termIndex > activeTermIndex;

              return (
                <div
                  key={termIndex}
                  className={[
                    'lcv-pt-node',
                    'lcv-pt-node-term',
                    termActive ? 'lcv-pt-node-active' : '',
                    termDone ? 'lcv-pt-node-done' : '',
                    termLocked ? 'lcv-pt-node-locked' : '',
                    celebratingIds.includes(`term-${termIndex}`)
                      ? 'lcv-pt-node-complete-pop'
                      : '',
                  ].filter(Boolean).join(' ')}
                >
                  <div className="lcv-pt-node-header">
                    <span className="lcv-pt-num">{termDone ? '✓' : termIndex + 1}</span>
                    <span className="lcv-pt-name">{term.term}</span>
                  </div>

                  <div className="lcv-pt-steps">
                    {STEPS.map((step, stepIndex) => {
                      const isDone = termDone || (termActive && stepIndex < currentStepIndex);
                      const isActive = termActive && stepIndex === currentStepIndex;
                      const isLocked = !isDone && !isActive;

                      return (
                        <button
                          key={step.key}
                          type="button"
                          className={[
                            'lcv-pt-step',
                            isActive ? 'lcv-pt-step-active' : '',
                            isDone ? 'lcv-pt-step-done' : '',
                            isLocked ? 'lcv-pt-step-locked' : '',
                          ].filter(Boolean).join(' ')}
                          style={isActive
                            ? {
                                borderColor: step.borderColor,
                                background: step.background,
                              }
                            : undefined}
                          disabled
                        >
                          <span className="lcv-pt-step-icon">{isDone ? '✓' : step.icon}</span>
                          <span className="lcv-pt-step-label">
                            {step.number}. {step.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            className={[
              'lcv-pt-cycle-btn',
              !isExpanded ? 'lcv-pt-cycle-spacer' : '',
              canScrollNext ? '' : 'lcv-pt-cycle-hidden',
            ].filter(Boolean).join(' ')}
            onClick={() =>
              setWindowStart(start => Math.min(Math.max(0, totalSlots - WINDOW), start + 1))
            }
            disabled={!canScrollNext || !isExpanded}
            aria-label="Show next terms"
          >
            ›
          </button>
        </div>

        <div
          className={[
            'lcv-pt-node',
            'lcv-pt-node-mastery',
            isMasteryActive ? 'lcv-pt-node-active' : '',
            isMasteryDone ? 'lcv-pt-node-done' : '',
            !masteryReached ? 'lcv-pt-node-locked' : '',
            celebratingIds.includes('mastery') ? 'lcv-pt-node-complete-pop' : '',
          ].filter(Boolean).join(' ')}
        >
          <div className="lcv-pt-node-header">
            <span className="lcv-pt-num">{isMasteryDone ? '✓' : '🏆'}</span>
            <span className="lcv-pt-name">Mastery</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LCVProgressTracker;
